/**
 * FauxDS SelectionCard - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate SelectionCard component for prototyping.
 * It is NOT the real @faire/slate SelectionCard and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate SelectionCard, see: @faire/slate/components/selection-card
 *
 * This component was recreated based on Slate design patterns:
 * - Design tokens extracted from Figma variables
 * - All variants verified: Selected (true/false), Disabled (true/false)
 * - Custom content support
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef } from "react";

export interface SelectionCardProps {
  /**
   * Whether the card is selected
   * @default false
   */
  checked?: boolean;
  /**
   * Whether the card is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * The title/label text
   */
  label: string;
  /**
   * Optional description text
   */
  description?: string;
  /**
   * Optional right-aligned content (e.g., price)
   */
  endSlot?: React.ReactNode;
  /**
   * Optional children to render below the title/description
   */
  children?: React.ReactNode;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * Input name for radio grouping
   */
  name?: string;
  /**
   * Value for the radio input
   */
  value?: string;
}

/**
 * SelectionCard - FauxDS (recreated from Figma MCP)
 *
 * SelectionCard is a clickable card with a radio indicator for single selection.
 * Used for payment options, shipping methods, or any single-choice selection UI.
 *
 * Usage tips:
 * - Use within a group for mutually exclusive options
 * - Provide clear title and optional description
 * - Use rightContent for prices or amounts
 */
const SelectionCard = forwardRef<HTMLDivElement, SelectionCardProps>(
  (
    {
      checked: selected = false,
      disabled = false,
      label: title,
      description,
      endSlot: rightContent,
      children,
      onClick,
      className = "",
      name,
      value,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = React.useState(false);

    const handleClick = () => {
      if (!disabled && onClick) {
        onClick();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && !disabled) {
        e.preventDefault();
        onClick?.();
      }
    };

    // Border styles based on state (matching Figma Slate Selection Card)
    // All states: 4px border radius
    // Unchecked: 1px border Action/Border/Subdued (#dfe0e1)
    // Hover (unselected): 1px border Action/Border/Hover (#000000)
    // Checked: 1px border Action/Border/Active (#000000)
    // Disabled: 1px border Action/Border/Disabled (#dfe0e1)
    const getBorderStyles = () => {
      if (disabled) {
        return "border border-[var(--slate-color-action-border-disabled,#dfe0e1)] rounded-[4px]";
      }
      if (selected) {
        return "border border-[var(--slate-color-action-border-active,#000000)] rounded-[4px]";
      }
      if (isHovered) {
        return "border border-[var(--slate-color-action-border-hover,#000000)] rounded-[4px]";
      }
      return "border border-[var(--slate-color-action-border-subdued,#dfe0e1)] rounded-[4px]";
    };

    // Background is always white for non-disabled states
    const getBackgroundColor = () => {
      if (disabled) return "bg-fs-surface-primary";
      return "bg-fs-surface-primary";
    };

    const getTextColor = () => {
      if (disabled) return "text-[#757575]";
      return "text-fs-text-primary";
    };

    const getDescriptionColor = () => {
      if (disabled) return "text-[#757575]";
      return "text-[#333333]";
    };

    // Radio circle styles (matching Figma Slate Radio)
    // Unchecked: 1px border #333333
    // Hover: 1px border #333333
    // Checked: 1px border #000000 with filled dot
    // Disabled: 1px border #dfe0e1
    const getRadioStyles = () => {
      if (disabled) {
        return {
          border: "border border-[#dfe0e1]",
          dot: selected ? "bg-[#dfe0e1]" : "",
        };
      }
      if (selected) {
        return {
          border: "border border-[#000000]",
          dot: "bg-[#000000]",
        };
      }
      return {
        border: "border border-[#333333]",
        dot: "",
      };
    };

    const radioStyles = getRadioStyles();

    return (
      <div
        ref={ref}
        role="radio"
        aria-checked={selected}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => !disabled && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          "relative flex items-start gap-3 p-4",
          getBorderStyles(),
          getBackgroundColor(),
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          "transition-all duration-150",
          className
        )}
      >
        {/* Hidden radio input for form compatibility */}
        <input
          type="radio"
          name={name}
          value={value}
          checked={selected}
          disabled={disabled}
          onChange={() => {}}
          className="sr-only"
          aria-hidden="true"
        />

        {/* Radio indicator - matches Slate Radio */}
        <div
          className={cn(
            "mt-0.5 h-5 w-5 shrink-0 rounded-full",
            radioStyles.border,
            "flex items-center justify-center transition-all duration-150"
          )}
        >
          {selected && (
            <div
              className={cn(
                "h-2.5 w-2.5 rounded-full",
                radioStyles.dot,
                "transition-all duration-150"
              )}
            />
          )}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <span
                className={cn("block", getTextColor())}
                style={{
                  fontFamily:
                    "var(--slate-typography-family-sans-serif, 'Graphik', sans-serif)",
                  fontSize: "var(--slate-typography-font-size-200, 14px)",
                  fontWeight: "var(--slate-typography-weight-medium, 500)",
                  lineHeight: "var(--slate-typography-line-height-200, 20px)",
                }}
              >
                {title}
              </span>
              {description && (
                <span
                  className={cn("mt-[4px] block", getDescriptionColor())}
                  style={{
                    fontFamily:
                      "var(--slate-typography-family-sans-serif, 'Graphik', sans-serif)",
                    fontSize: "var(--slate-typography-font-size-200, 14px)",
                    fontWeight: "var(--slate-typography-weight-regular, 400)",
                    lineHeight: "var(--slate-typography-line-height-200, 20px)",
                  }}
                >
                  {description}
                </span>
              )}
            </div>
            {rightContent && (
              <div
                className={cn("shrink-0", getTextColor())}
                style={{
                  fontFamily:
                    "var(--slate-typography-family-sans-serif, 'Graphik', sans-serif)",
                  fontSize: "var(--slate-typography-font-size-200, 14px)",
                  fontWeight: "var(--slate-typography-weight-medium, 500)",
                  lineHeight: "var(--slate-typography-line-height-200, 20px)",
                }}
              >
                {rightContent}
              </div>
            )}
          </div>
          {children && <div className="mt-2">{children}</div>}
        </div>
      </div>
    );
  }
);

SelectionCard.displayName = "SelectionCard";

export default SelectionCard;
